// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import GameMode from '@app/features/accessibility/state/GameMode';
import {subscribeSaveData} from '@app/features/app/hooks/useSaveData';
import {observeIntersection} from '@app/features/platform/utils/SharedIntersectionObserver';
import Window from '@app/features/window/state/Window';
import {makeAutoObservable} from 'mobx';

// Micup akis-v3: feed videos play by themselves, muted, as they scroll into view (X, Instagram). One video at a time:
// the most visible one of those shown at least this much.
export const AUTOPLAY_MIN_VISIBLE_RATIO = 0.6;
export const AUTOPLAY_THRESHOLDS = [0, AUTOPLAY_MIN_VISIBLE_RATIO, 1];
// A video scrolled past faster than this fetches nothing: the turn goes to one that stays the most visible this long.
export const AUTOPLAY_START_DELAY_MS = 300;

const readSaveData = (): boolean =>
	typeof navigator !== 'undefined' &&
	(navigator as Navigator & {connection?: {saveData?: boolean}}).connection?.saveData === true;

class FeedAutoplay {
	// For the session only: a viewer who turns the sound on hears the next video too; a reload starts muted again.
	muted = true;
	volume = 1;
	activeId: string | null = null;
	// A video the viewer started: it plays on with the window unfocused or another video more in view, until it leaves.
	claimedId: string | null = null;
	saveData = readSaveData();
	// The last ratio each registered video reported; it changes only at the thresholds, so it is not observed.
	private ratios = new Map<string, number>();
	// The most visible video now; it becomes activeId once it has led for AUTOPLAY_START_DELAY_MS (one timer for all).
	private leadId: string | null = null;
	private leadTimer: ReturnType<typeof setTimeout> | null = null;

	constructor() {
		makeAutoObservable<this, 'ratios' | 'leadId' | 'leadTimer'>(
			this,
			{ratios: false, leadId: false, leadTimer: false},
			{autoBind: true},
		);
		subscribeSaveData(this.setSaveData);
	}

	// Reduced motion, data saver, lite mode, a hidden page or another window in front: the poster and its
	// play button only.
	get allowed(): boolean {
		return !Accessibility.useReducedMotion && !this.saveData && !GameMode.lightMode && Window.visible && Window.focused;
	}

	isActive(id: string): boolean {
		if (this.claimedId !== null) return this.claimedId === id;
		return this.allowed && this.activeId === id;
	}

	claim(id: string): void {
		this.claimedId = id;
	}

	unclaim(id: string): void {
		if (this.claimedId === id) this.claimedId = null;
	}

	setMuted(muted: boolean): void {
		this.muted = muted;
	}

	setVolume(volume: number): void {
		this.volume = Math.max(0, Math.min(1, volume));
		this.muted = this.volume === 0;
	}

	setSaveData(saveData: boolean): void {
		this.saveData = saveData;
	}

	register(id: string, element: Element): () => void {
		const stop = observeIntersection(
			element,
			(entry) => this.handleRatio(id, entry.isIntersecting ? entry.intersectionRatio : 0),
			{threshold: AUTOPLAY_THRESHOLDS},
		);
		return () => {
			stop();
			this.handleRatio(id, 0);
		};
	}

	// The leading video keeps its lead until another one is shown more than it is.
	handleRatio(id: string, ratio: number): void {
		if (ratio > 0) this.ratios.set(id, ratio);
		else {
			this.ratios.delete(id);
			this.unclaim(id);
		}
		let best: string | null = null;
		let bestRatio = 0;
		const current = this.leadId === null ? undefined : this.ratios.get(this.leadId);
		if (current !== undefined && current >= AUTOPLAY_MIN_VISIBLE_RATIO) {
			best = this.leadId;
			bestRatio = current;
		}
		for (const [key, value] of this.ratios) {
			if (value >= AUTOPLAY_MIN_VISIBLE_RATIO && value > bestRatio) {
				best = key;
				bestRatio = value;
			}
		}
		if (best === this.leadId) return;
		this.leadId = best;
		if (this.leadTimer !== null) clearTimeout(this.leadTimer);
		this.leadTimer = null;
		if (best === null || best === this.activeId) {
			this.activeId = best;
			return;
		}
		this.leadTimer = setTimeout(this.promoteLead, AUTOPLAY_START_DELAY_MS);
	}

	private promoteLead(): void {
		this.leadTimer = null;
		this.activeId = this.leadId;
	}
}

export default new FeedAutoplay();
